import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDepartment, type OrgEmployee } from "./types";

interface OrgDashboardViewProps {
  employees: OrgEmployee[];
  directReportsByManager: Map<string, OrgEmployee[]>;
  subtreeSizeById: Map<string, number>;
}

const SPAN_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "1-3 diretos", min: 1, max: 3 },
  { label: "4-5 diretos", min: 4, max: 5 },
  { label: "6-7 diretos", min: 6, max: 7 },
  { label: "8+ diretos", min: 8, max: Number.POSITIVE_INFINITY },
];

// Bucket labels MUST match the strings produced by the server-side computation in
// server/dal/employees.ts (tenureBucketLabel / ageBucketLabel). Server-side bucketing
// keeps raw birthdate/startDate off the API surface (LGPD).
const TENURE_BUCKET_LABELS = ["<1 ano", "1–3 anos", "3–5 anos", "5+ anos"] as const;
const AGE_BUCKET_LABELS = ["18–25", "26–35", "36–45", "46+"] as const;

const SECONDARY_COLOR = "hsl(215 20% 55%)"; // muted slate
const COMPANY_COLOR = "hsl(215 16% 75%)"; // lighter slate for "Empresa" comparison row

// ---------- Helpers ----------

function pct(part: number, total: number): number {
  if (total === 0) return 0;
  return (part / total) * 100;
}

function fmtPct(n: number): string {
  return `${n.toFixed(0)}%`;
}

// ---------- Stats ----------

interface SubsetStats {
  population: OrgEmployee[];
  totalManagers: number;
  totalIcs: number;
  totalPopulation: number;
  populationPct: number;
  avgSpan: number;
  minSpan: number;
  maxSpan: number;
  avgTeamSize: number;
  maxTeam: number;
  depthLevels: number;
  spanBuckets: BucketRow[];
  gradeRows: { grade: string; count: number; pct: number }[];
  tenureBuckets: BucketRow[];
  ageBuckets: BucketRow[];
  unknownTenure: number;
  unknownAge: number;
}

interface BucketRow {
  label: string;
  count: number;
  pct: number;
}

function computeSubsetStats(
  population: OrgEmployee[],
  directReportsByManager: Map<string, OrgEmployee[]>,
  subtreeSizeById: Map<string, number>,
  totalForPopulationPct: number,
): SubsetStats {
  const popIds = new Set(population.map((e) => e.id));

  // Span counts using directReportsByManager — but only count reports inside the subset
  const spans: number[] = [];
  const subtreeSizes: number[] = [];
  const managerIds: string[] = [];
  for (const e of population) {
    const reports = (directReportsByManager.get(e.id) ?? []).filter((r) => popIds.has(r.id));
    if (reports.length > 0) {
      managerIds.push(e.id);
      spans.push(reports.length);
      // subtree size — use precomputed, since it's transitive over the whole tree.
      // When filtering by department, this is "the team size as visible in the full org",
      // which is the meaningful interpretation (a Director of Eng still has the whole eng tree below).
      subtreeSizes.push(subtreeSizeById.get(e.id) ?? 0);
    }
  }
  const totalManagers = managerIds.length;
  const totalPopulation = population.length;
  const totalIcs = totalPopulation - totalManagers;

  const avgSpan = totalManagers === 0 ? 0 : spans.reduce((a, b) => a + b, 0) / totalManagers;
  const minSpan = totalManagers === 0 ? 0 : Math.min(...spans);
  const maxSpan = totalManagers === 0 ? 0 : Math.max(...spans);

  const avgTeamSize =
    totalManagers === 0 ? 0 : subtreeSizes.reduce((a, b) => a + b, 0) / totalManagers;
  const maxTeam = totalManagers === 0 ? 0 : Math.max(...subtreeSizes);

  // Depth — relative to the global tree (we don't recompute when filtered)
  const employeeById = new Map(population.map((e) => [e.id, e]));
  const depthCache = new Map<string, number>();
  const depthOf = (id: string): number => {
    const cached = depthCache.get(id);
    if (cached !== undefined) return cached;
    const emp = employeeById.get(id);
    if (!emp) return 0;
    if (!emp.directManagerId || !employeeById.has(emp.directManagerId)) {
      depthCache.set(id, 0);
      return 0;
    }
    const d = depthOf(emp.directManagerId) + 1;
    depthCache.set(id, d);
    return d;
  };
  let maxDepth = 0;
  for (const e of population) {
    const d = depthOf(e.id);
    if (d > maxDepth) maxDepth = d;
  }
  const depthLevels = totalPopulation === 0 ? 0 : maxDepth + 1;

  // Span buckets
  const spanBuckets = SPAN_BUCKETS.map((b) => {
    const count = spans.filter((s) => s >= b.min && s <= b.max).length;
    return { label: b.label, count, pct: pct(count, totalManagers) };
  });

  // Grade rows — keep slot for L0..L7 explicitly so empty levels render.
  const byGrade = new Map<string, number>();
  for (const e of population) {
    const label = e.jobLevel ?? "Sem nível";
    byGrade.set(label, (byGrade.get(label) ?? 0) + 1);
  }
  const knownLevels = ["L7", "L6", "L5", "L4", "L3", "L2", "L1", "L0"];
  const gradeRows: { grade: string; count: number; pct: number }[] = [];
  for (const lvl of knownLevels) {
    const count = byGrade.get(lvl) ?? 0;
    gradeRows.push({ grade: lvl, count, pct: pct(count, totalPopulation) });
  }
  if (byGrade.has("Sem nível")) {
    const c = byGrade.get("Sem nível") ?? 0;
    gradeRows.push({ grade: "Sem nível", count: c, pct: pct(c, totalPopulation) });
  }

  // Tenure & age buckets — the server already pre-computed each employee's bucket label
  // (tenureBucket / ageBucket) so we just count occurrences here.
  const tenureCount = new Map<string, number>();
  let unknownTenure = 0;
  for (const e of population) {
    if (!e.tenureBucket) {
      unknownTenure += 1;
      continue;
    }
    tenureCount.set(e.tenureBucket, (tenureCount.get(e.tenureBucket) ?? 0) + 1);
  }
  const knownTenureTotal = totalPopulation - unknownTenure;
  const tenureBuckets: BucketRow[] = TENURE_BUCKET_LABELS.map((label) => {
    const count = tenureCount.get(label) ?? 0;
    return { label, count, pct: pct(count, knownTenureTotal) };
  });

  const ageCount = new Map<string, number>();
  let unknownAge = 0;
  for (const e of population) {
    if (!e.ageBucket) {
      unknownAge += 1;
      continue;
    }
    ageCount.set(e.ageBucket, (ageCount.get(e.ageBucket) ?? 0) + 1);
  }
  const knownAgeTotal = totalPopulation - unknownAge;
  const ageBuckets: BucketRow[] = AGE_BUCKET_LABELS.map((label) => {
    const count = ageCount.get(label) ?? 0;
    return { label, count, pct: pct(count, knownAgeTotal) };
  });

  return {
    population,
    totalManagers,
    totalIcs,
    totalPopulation,
    populationPct: pct(totalPopulation, totalForPopulationPct),
    avgSpan,
    minSpan,
    maxSpan,
    avgTeamSize,
    maxTeam,
    depthLevels,
    spanBuckets,
    gradeRows,
    tenureBuckets,
    ageBuckets,
    unknownTenure,
    unknownAge,
  };
}

// ---------- Component ----------

const FILTER_PARAM = "dept";

function getFilterFromUrl(): string {
  if (typeof window === "undefined") return "all";
  return new URLSearchParams(window.location.search).get(FILTER_PARAM) ?? "all";
}

export function OrgDashboardView({
  employees,
  directReportsByManager,
  subtreeSizeById,
}: OrgDashboardViewProps) {
  const [filter, setFilter] = useState<string>(getFilterFromUrl);

  const updateFilter = useCallback((next: string) => {
    setFilter(next);
    const url = new URL(window.location.href);
    if (next === "all") url.searchParams.delete(FILTER_PARAM);
    else url.searchParams.set(FILTER_PARAM, next);
    window.history.replaceState({}, "", url.toString());
  }, []);

  // Available department options — sorted by employee count desc
  const departmentOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of employees) {
      if (e.department) counts.set(e.department, (counts.get(e.department) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, label: formatDepartment(value), count }))
      .sort((a, b) => b.count - a.count);
  }, [employees]);

  // If filter no longer matches a known department (data refreshed), reset.
  useEffect(() => {
    if (filter !== "all" && !departmentOptions.some((d) => d.value === filter)) {
      updateFilter("all");
    }
  }, [filter, departmentOptions, updateFilter]);

  const companyStats = useMemo(
    () => computeSubsetStats(employees, directReportsByManager, subtreeSizeById, employees.length),
    [employees, directReportsByManager, subtreeSizeById],
  );

  const filteredPopulation = useMemo(
    () => (filter === "all" ? employees : employees.filter((e) => e.department === filter)),
    [employees, filter],
  );

  const areaStats = useMemo(
    () =>
      filter === "all"
        ? companyStats
        : computeSubsetStats(
            filteredPopulation,
            directReportsByManager,
            subtreeSizeById,
            employees.length,
          ),
    [
      filter,
      filteredPopulation,
      directReportsByManager,
      subtreeSizeById,
      employees.length,
      companyStats,
    ],
  );

  const isFiltered = filter !== "all";
  const filterLabel = isFiltered
    ? (departmentOptions.find((d) => d.value === filter)?.label ?? filter)
    : "Toda a empresa";

  // Composição & Gerentes por Área charts (only meaningful when not filtered)
  const compositionRows = useMemo(() => {
    if (isFiltered) return [];
    const byDept = new Map<string, number>();
    for (const e of employees) {
      const label = formatDepartment(e.department);
      byDept.set(label, (byDept.get(label) ?? 0) + 1);
    }
    const total = employees.length;
    return Array.from(byDept.entries())
      .map(([label, count]) => ({ label, count, pct: pct(count, total) }))
      .sort((a, b) => b.count - a.count);
  }, [employees, isFiltered]);

  const managersByDeptRows = useMemo(() => {
    if (isFiltered) return [];
    const byDept = new Map<string, number>();
    for (const e of employees) {
      const reports = directReportsByManager.get(e.id) ?? [];
      if (reports.length === 0) continue;
      const label = formatDepartment(e.department);
      byDept.set(label, (byDept.get(label) ?? 0) + 1);
    }
    return Array.from(byDept.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [employees, directReportsByManager, isFiltered]);

  if (companyStats.totalPopulation === 0) {
    return (
      <div className="flex-1 flex items-center justify-center py-16 text-muted-foreground text-sm">
        Sem colaboradores ativos para analisar.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 pb-10">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card shadow-sm p-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Recorte:</span>
          <Select value={filter} onValueChange={updateFilter}>
            <SelectTrigger className="w-[220px]" data-testid="select-org-dashboard-area">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda a empresa</SelectItem>
              {departmentOptions.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label} ({d.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isFiltered && (
          <div className="text-xs text-muted-foreground">
            Comparando <span className="font-semibold text-foreground">{filterLabel}</span> com a
            empresa
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          title="Total de Pessoas"
          value={String(areaStats.totalPopulation)}
          subtitle={
            isFiltered
              ? `${fmtPct(areaStats.populationPct)} da empresa`
              : "colaboradores ativos no organograma"
          }
          chips={[
            { label: "Gerentes", value: areaStats.totalManagers },
            { label: "ICs", value: areaStats.totalIcs },
          ]}
        />
        <KpiCard
          title="Span of Control Médio"
          value={areaStats.avgSpan.toFixed(1)}
          subtitle={
            isFiltered
              ? `Empresa: ${companyStats.avgSpan.toFixed(1)}`
              : "colaboradores diretos por gerente"
          }
          chips={[
            { label: "Mínimo", value: areaStats.minSpan },
            { label: "Máximo", value: areaStats.maxSpan },
          ]}
        />
        <KpiCard
          title="Tamanho Médio de Equipe"
          value={areaStats.avgTeamSize.toFixed(1)}
          subtitle={
            isFiltered
              ? `Empresa: ${companyStats.avgTeamSize.toFixed(1)}`
              : "colaboradores totais por gerente"
          }
          chips={[
            { label: "Maior equipe", value: areaStats.maxTeam },
            { label: "Profundidade", value: areaStats.depthLevels },
          ]}
        />
      </div>

      {/* HERO — Span of Control */}
      <SectionHeader
        eyebrow="Span of Control"
        title="Distribuição de gestão"
        description="Quantos reportes diretos cada gerente tem na empresa."
      />
      <Card className="-mt-4">
        <CardHeader>
          <CardTitle className="text-lg">Distribuição de Span of Control</CardTitle>
          <p className="text-xs text-muted-foreground">
            Gerentes agrupados por faixa de reportes diretos.
          </p>
        </CardHeader>
        <CardContent>
          <BarList rows={areaStats.spanBuckets} tooltipUnit="Gerentes" tall />
        </CardContent>
      </Card>

      {/* Forma do org — só Estrutura por Grade após remoção de Camadas Hierárquicas */}
      <SectionHeader
        eyebrow="Estrutura"
        title="Pirâmide de senioridade"
        description="Distribuição da população por nível de senioridade, independente da posição na hierarquia de reportes."
      />
      <Card className="-mt-4">
        <CardHeader>
          <CardTitle className="text-base">Estrutura por Grade</CardTitle>
          <p className="text-xs text-muted-foreground">
            Pirâmide de níveis (sênior no topo). Largura da barra = % do{" "}
            {isFiltered ? "recorte" : "total"}.
          </p>
        </CardHeader>
        <CardContent>
          <GradePyramid
            area={areaStats.gradeRows}
            company={isFiltered ? companyStats.gradeRows : null}
            areaLabel={filterLabel}
          />
        </CardContent>
      </Card>

      {/* Distribuição — Composição por Área + Gerentes por Área (only when no filter) */}
      {!isFiltered && (
        <>
          <SectionHeader
            eyebrow="Distribuição"
            title="Áreas e gestão"
            description="Onde estão as pessoas e onde mora a gestão."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 -mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Composição por Área</CardTitle>
                <p className="text-xs text-muted-foreground">Headcount total por área</p>
              </CardHeader>
              <CardContent>
                <DemoBars rows={compositionRows} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Gerentes por Área</CardTitle>
                <p className="text-xs text-muted-foreground">Pontos de gestão por área</p>
              </CardHeader>
              <CardContent>
                <BarList
                  rows={managersByDeptRows.map((r) => ({ ...r, pct: 0 }))}
                  tooltipUnit="Gerentes"
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Demografia */}
      <SectionHeader
        eyebrow="Composição"
        title="Demografia agregada"
        description="Distribuições de antiguidade e idade — sempre agregado, nunca individual."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 -mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tempo de Empresa</CardTitle>
            <p className="text-xs text-muted-foreground">
              Distribuição por antiguidade
              {areaStats.unknownTenure > 0 ? ` · ${areaStats.unknownTenure} sem data` : ""}
            </p>
          </CardHeader>
          <CardContent>
            {isFiltered ? (
              <ComparisonBars
                area={areaStats.tenureBuckets}
                company={companyStats.tenureBuckets}
                areaLabel={filterLabel}
              />
            ) : (
              <DemoBars rows={areaStats.tenureBuckets} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Faixa Etária</CardTitle>
            <p className="text-xs text-muted-foreground">
              Distribuição por idade
              {areaStats.unknownAge > 0 ? ` · ${areaStats.unknownAge} sem data` : ""}
            </p>
          </CardHeader>
          <CardContent>
            {isFiltered ? (
              <ComparisonBars
                area={areaStats.ageBuckets}
                company={companyStats.ageBuckets}
                areaLabel={filterLabel}
              />
            ) : (
              <DemoBars rows={areaStats.ageBuckets} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function SectionHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col gap-1 mt-2">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary">
        {eyebrow}
      </div>
      <h2 className="text-lg font-semibold leading-tight">{title}</h2>
      {description && <p className="text-sm text-muted-foreground max-w-2xl">{description}</p>}
    </div>
  );
}

function KpiCard({
  title,
  value,
  subtitle,
  chips,
}: {
  title: string;
  value: string;
  subtitle?: string;
  chips?: { label: string; value: number }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="text-4xl font-bold text-primary tabular-nums">{value}</div>
        {subtitle && <div className="text-sm text-muted-foreground">{subtitle}</div>}
        {chips && chips.length > 0 && (
          <div className="flex gap-2 mt-2">
            {chips.map((c) => (
              <div key={c.label} className="flex-1 rounded-md border bg-muted/40 px-3 py-2">
                <div className="text-xl font-semibold text-primary tabular-nums">{c.value}</div>
                <div className="text-xs text-muted-foreground">{c.label}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type BarRow = { label: string; count: number; pct?: number };

interface BarListProps {
  rows: BarRow[];
  colorFor?: (row: BarRow) => string;
  tooltipUnit?: string;
  tall?: boolean;
}

function BarList({
  rows,
  colorFor = () => SECONDARY_COLOR,
  tooltipUnit = "Pessoas",
  tall = false,
}: BarListProps) {
  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground">Sem dados.</div>;
  }
  const rowHeight = tall ? 56 : 36;
  const minHeight = tall ? 260 : 180;
  return (
    <div style={{ width: "100%", height: Math.max(minHeight, rows.length * rowHeight + 16) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 4, right: 32, bottom: 4, left: 4 }}
          barCategoryGap={tall ? 14 : 8}
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            width={tall ? 140 : 120}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: tall ? 13 : 12, fill: "hsl(var(--foreground))" }}
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted))" }}
            contentStyle={{
              background: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              borderRadius: 6,
              fontSize: 12,
            }}
            formatter={(value: number) => [value, tooltipUnit]}
            labelFormatter={(l) => String(l)}
          />
          <Bar
            dataKey="count"
            radius={[6, 6, 6, 6]}
            label={{ position: "right", fontSize: tall ? 14 : 12, fontWeight: 600 }}
          >
            {rows.map((r) => (
              <Cell key={r.label} fill={colorFor(r)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Bar list with percentage rendered alongside count — HTML demographic style.
function DemoBars({ rows }: { rows: BarRow[] }) {
  if (rows.length === 0 || rows.every((r) => r.count === 0)) {
    return <div className="text-sm text-muted-foreground">Sem dados.</div>;
  }
  const maxPct = Math.max(...rows.map((r) => r.pct ?? 0), 1);
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => {
        const widthPct = r.pct !== undefined ? (r.pct / maxPct) * 100 : 0;
        return (
          <div key={r.label} className="grid grid-cols-[100px_1fr_72px] items-center gap-3">
            <div className="text-sm text-foreground truncate">{r.label}</div>
            <div className="relative h-3 bg-muted rounded">
              <div
                className="absolute inset-y-0 left-0 rounded"
                style={{ width: `${widthPct}%`, background: SECONDARY_COLOR }}
              />
            </div>
            <div className="text-sm font-semibold tabular-nums text-right">
              {r.count}
              {r.pct !== undefined && (
                <span className="text-xs text-muted-foreground ml-1">({fmtPct(r.pct)})</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Two-row comparison: Area row (primary slate) above Company row (lighter slate).
function ComparisonBars({
  area,
  company,
  areaLabel,
}: {
  area: BarRow[];
  company: BarRow[];
  areaLabel: string;
}) {
  if (area.length === 0) {
    return <div className="text-sm text-muted-foreground">Sem dados.</div>;
  }
  const companyByLabel = new Map(company.map((r) => [r.label, r]));
  const labels = Array.from(new Set([...area.map((r) => r.label), ...company.map((r) => r.label)]));
  const areaByLabel = new Map(area.map((r) => [r.label, r]));
  const maxPct = Math.max(...area.map((r) => r.pct ?? 0), ...company.map((r) => r.pct ?? 0), 1);
  return (
    <div className="flex flex-col gap-4">
      {labels.map((label) => {
        const a = areaByLabel.get(label) ?? { label, count: 0, pct: 0 };
        const c = companyByLabel.get(label) ?? { label, count: 0, pct: 0 };
        return (
          <div key={label} className="grid grid-cols-[100px_1fr] items-center gap-3">
            <div className="text-sm text-foreground truncate">{label}</div>
            <div className="flex flex-col gap-1">
              <ComparisonBarRow
                tag={areaLabel}
                tagColor={SECONDARY_COLOR}
                pct={a.pct ?? 0}
                count={a.count}
                maxPct={maxPct}
                emphasized
              />
              <ComparisonBarRow
                tag="Empresa"
                tagColor={COMPANY_COLOR}
                pct={c.pct ?? 0}
                count={c.count}
                maxPct={maxPct}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ComparisonBarRow({
  tag,
  tagColor,
  pct: pctValue,
  count,
  maxPct,
  emphasized = false,
}: {
  tag: string;
  tagColor: string;
  pct: number;
  count: number;
  maxPct: number;
  emphasized?: boolean;
}) {
  const widthPct = (pctValue / maxPct) * 100;
  return (
    <div className="grid grid-cols-[80px_1fr_64px] items-center gap-2 text-xs">
      <span
        className={`uppercase tracking-wide font-semibold truncate ${emphasized ? "" : "text-muted-foreground"}`}
        style={emphasized ? { color: tagColor } : undefined}
      >
        {tag}
      </span>
      <div className="relative h-2 bg-muted rounded">
        <div
          className="absolute inset-y-0 left-0 rounded"
          style={{ width: `${widthPct}%`, background: tagColor }}
        />
      </div>
      <span
        className={`tabular-nums text-right ${emphasized ? "font-semibold text-foreground" : "text-muted-foreground"}`}
      >
        {count} ({fmtPct(pctValue)})
      </span>
    </div>
  );
}

// Grade pyramid — L7 top → L0 bottom, optional company benchmark marker.
function GradePyramid({
  area,
  company,
  areaLabel,
}: {
  area: { grade: string; count: number; pct: number }[];
  company: { grade: string; count: number; pct: number }[] | null;
  areaLabel: string;
}) {
  if (area.length === 0 || area.every((g) => g.count === 0)) {
    return <div className="text-sm text-muted-foreground">Sem dados.</div>;
  }
  const maxPct = Math.max(...area.map((g) => g.pct), 1);
  const companyByGrade = new Map(company?.map((g) => [g.grade, g]) ?? []);
  return (
    <div className="flex flex-col gap-1.5">
      {area.map((g) => {
        const widthPct = (g.pct / maxPct) * 100;
        const cmp = companyByGrade.get(g.grade);
        return (
          <div key={g.grade} className="grid grid-cols-[60px_1fr_88px] items-center gap-3 py-1">
            <div className="text-sm font-semibold tabular-nums">{g.grade}</div>
            <div className="relative h-5 bg-muted rounded">
              {g.count > 0 ? (
                <div
                  className="absolute inset-y-0 left-0 rounded flex items-center justify-end pr-2 text-[11px] font-semibold text-white"
                  style={{
                    width: `${Math.max(widthPct, 8)}%`,
                    background: SECONDARY_COLOR,
                  }}
                >
                  {g.count}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground absolute inset-0 flex items-center px-2">
                  Sem pessoas neste nível
                </div>
              )}
              {cmp && cmp.pct > 0 && (
                <div
                  title={`Empresa: ${fmtPct(cmp.pct)}`}
                  className="absolute -top-1 -bottom-1 w-0.5 bg-primary"
                  style={{ left: `${Math.min((cmp.pct / maxPct) * 100, 100)}%` }}
                >
                  <span className="absolute -top-2 -translate-x-1/2 left-0">
                    <span className="block w-2 h-2 rounded-full bg-primary" />
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums">{fmtPct(g.pct)}</div>
              {cmp && (
                <div className="text-[10px] text-muted-foreground">empresa: {fmtPct(cmp.pct)}</div>
              )}
            </div>
          </div>
        );
      })}
      {company && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 pt-3 border-t mt-2 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block w-3 h-2 rounded-sm"
              style={{ background: SECONDARY_COLOR }}
            />
            <span className="font-semibold text-foreground">{areaLabel}</span> (% do recorte)
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-primary" />
            Marcador empresa (% do total)
          </span>
        </div>
      )}
    </div>
  );
}
